import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Button,
  Icon,
  Dialog,
  Classes,
  Intent,
  InputGroup,
  TextArea,
  FormGroup,
  RadioGroup,
  Radio,
  Tag,
  NonIdealState,
  Spinner,
  Menu,
  MenuItem,
  Popover,
  Position,
} from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { useAppStore } from '../../stores/UnifiedStoreProvider';
import { TicketType, TicketStatus, TicketPriority } from '../../stores/TicketStore';
import Comments from '../comments/Comments';
import { ToastService } from '../../services/ToastService';
import './IssueTracking.css';

/**
 * Component for displaying and managing issue tracking in the sidebar
 */
const IssueTracking: React.FC = observer(() => {
  const appStore = useAppStore();
  const { tickets, auth } = appStore;
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Form state for new ticket
  const [newTicket, setNewTicket] = useState({
    title: '',
    description: '',
    type: TicketType.FEEDBACK,
    priority: TicketPriority.MEDIUM,
    tags: [] as string[],
  });

  // Get the selected ticket object
  const selectedTicket = selectedTicketId ? tickets.ticketsArray.find(t => t.id === selectedTicketId) : null;

  // Debug: Log auth and ticket store state
  useEffect(() => {
    console.log('🔍 [IssueTracking] Debug Info:', {
      userRole: auth.user?.role,
      isAdmin: tickets.isAdmin,
      userEmail: auth.user?.email,
      authUser: auth.user,
    });
  }, [auth.user, tickets.isAdmin]);

  // Load tickets when component mounts and auth is ready
  useEffect(() => {
    if (auth.isReallyAuthenticated) {
      tickets.fetchTickets();
    }
  }, [auth.isReallyAuthenticated]);

  // Debug: Log when isCreateDialogOpen changes
  useEffect(() => {
    console.log('🎬 [IssueTracking] isCreateDialogOpen changed:', tickets.isCreateDialogOpen);
  }, [tickets.isCreateDialogOpen]);

  const handleCreateTicket = async () => {
    if (!newTicket.title.trim() || !newTicket.description.trim()) {
      ToastService.show({ 
        message: 'Please provide both title and description',
        intent: Intent.WARNING 
      });
      return;
    }

    const created = await tickets.createTicket(newTicket);
    if (created) {
      ToastService.show({ 
        message: 'Ticket created successfully',
        intent: Intent.SUCCESS 
      });
      tickets.setCreateDialogOpen(false);
      setNewTicket({
        title: '',
        description: '',
        type: TicketType.FEEDBACK,
        priority: TicketPriority.MEDIUM,
        tags: [],
      });
    }
  };

  const handleStatusChange = async (ticketId: string, status: TicketStatus) => {
    const success = await tickets.updateTicketStatus(ticketId, status);
    if (success) {
      ToastService.show({ 
        message: 'Status updated',
        intent: Intent.SUCCESS 
      });
    }
  };

  const handleDeleteTicket = async (ticketId: string) => {
    if (!window.confirm('Are you sure you want to delete this ticket? This action cannot be undone.')) {
      return;
    }

    const success = await tickets.deleteTicket(ticketId);
    if (success) {
      ToastService.show({ 
        message: 'Ticket deleted',
        intent: Intent.SUCCESS 
      });
      // Close dialog if it's open
      if (selectedTicketId === ticketId) {
        setSelectedTicketId(null);
      }
    }
  };

  const getStatusIcon = (status: TicketStatus) => {
    switch (status) {
      case TicketStatus.BACKLOG:
        return IconNames.TIME;
      case TicketStatus.IN_PROGRESS:
        return IconNames.PLAY;
      case TicketStatus.RESOLVED:
        return IconNames.TICK_CIRCLE;
      default:
        return IconNames.CIRCLE;
    }
  };

  const getStatusColor = (status: TicketStatus) => {
    switch (status) {
      case TicketStatus.BACKLOG:
        return '#8B92A8';
      case TicketStatus.IN_PROGRESS:
        return '#FFA500';
      case TicketStatus.RESOLVED:
        return '#10B981';
      default:
        return '#6B7280';
    }
  };

  const getTypeIcon = (type: TicketType) => {
    switch (type) {
      case TicketType.BUG_REPORT:
        return IconNames.BUG;
      case TicketType.FEATURE_REQUEST:
        return IconNames.LIGHTBULB;
      case TicketType.QUESTION:
        return IconNames.HELP;
      case TicketType.FEEDBACK:
        return IconNames.COMMENT;
      default:
        return IconNames.ISSUE;
    }
  };

  const getPriorityColor = (priority: TicketPriority) => {
    switch (priority) {
      case TicketPriority.URGENT:
        return Intent.DANGER;
      case TicketPriority.HIGH:
        return Intent.WARNING;
      case TicketPriority.MEDIUM:
        return Intent.PRIMARY;
      case TicketPriority.LOW:
        return Intent.NONE;
      default:
        return Intent.NONE;
    }
  };

  return (
    <div className="issue-tracking-section">
      <div className="issue-tracking-header">
        <div 
          className="issue-tracking-title"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Icon icon={isExpanded ? IconNames.CHEVRON_DOWN : IconNames.CHEVRON_RIGHT} />
          <span>Issue Tracking</span>
        </div>
        <Button
          icon={IconNames.PLUS}
          minimal
          small
          onClick={() => tickets.setCreateDialogOpen(true)}
          title="Report Issue"
        />
      </div>

      {isExpanded && (
        <div className="issue-tracking-content">
          {tickets.loading ? (
            <div className="issue-tracking-loading">
              <Spinner size={20} />
            </div>
          ) : tickets.ticketsArray.length === 0 ? (
            <NonIdealState
              icon={IconNames.INBOX}
              title="No issues yet"
              description="Click + to report an issue"
              className="issue-tracking-empty"
            />
          ) : (
            <div className="issue-tracking-list">
              {tickets.filteredTickets.map((ticket) => (
                <div 
                  key={ticket.id}
                  className="issue-tracking-item"
                  onClick={() => setSelectedTicketId(ticket.id)}
                >
                  <div className="issue-item-header">
                    <Icon 
                      icon={getTypeIcon(ticket.type)} 
                      size={14}
                      color={getStatusColor(ticket.status)}
                    />
                    <span className="issue-item-title">{ticket.title}</span>
                    {tickets.isAdmin && (
                      <Popover
                        content={
                          <Menu>
                            <MenuItem
                              icon={IconNames.TRASH}
                              text="Delete Ticket"
                              intent={Intent.DANGER}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteTicket(ticket.id);
                              }}
                            />
                          </Menu>
                        }
                        position={Position.BOTTOM_RIGHT}
                      >
                        <Button 
                          icon={IconNames.MORE} 
                          minimal 
                          small 
                          onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        />
                      </Popover>
                    )}
                  </div>
                  <div className="issue-item-meta">
                    <Tag 
                      minimal 
                      intent={getPriorityColor(ticket.priority)}
                      className="issue-priority-tag"
                    >
                      {ticket.priority}
                    </Tag>
                    <span 
                      className="issue-status-indicator"
                      style={{ backgroundColor: getStatusColor(ticket.status) }}
                    />
                    <span className="issue-status-text">{ticket.status.replace('_', ' ')}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Create Ticket Dialog */}
      <Dialog
        isOpen={tickets.isCreateDialogOpen}
        onClose={() => tickets.setCreateDialogOpen(false)}
        title="Feedback"
        className="create-ticket-dialog"
      >
        <div className={Classes.DIALOG_BODY}>
          <FormGroup label="Title" labelInfo="(required)">
            <InputGroup
              value={newTicket.title}
              onChange={(e) => setNewTicket({ ...newTicket, title: e.target.value })}
              placeholder="Brief description of the issue"
              autoFocus
            />
          </FormGroup>

          <FormGroup label="Description" labelInfo="(required)">
            <TextArea
              value={newTicket.description}
              onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
              placeholder="Provide more details about the issue..."
              rows={4}
              fill
              growVertically
            />
          </FormGroup>

          <FormGroup label="Type">
            <RadioGroup
              onChange={(e) => setNewTicket({ ...newTicket, type: (e.target as HTMLInputElement).value as TicketType })}
              selectedValue={newTicket.type}
            >
              <Radio label="Question" value={TicketType.QUESTION} />
              <Radio label="Feature Request" value={TicketType.FEATURE_REQUEST} />
              <Radio label="Bug Report" value={TicketType.BUG_REPORT} />
              <Radio label="Feedback" value={TicketType.FEEDBACK} />
            </RadioGroup>
          </FormGroup>

          <FormGroup label="Priority">
            <RadioGroup
              onChange={(e) => setNewTicket({ ...newTicket, priority: (e.target as HTMLInputElement).value as TicketPriority })}
              selectedValue={newTicket.priority}
              inline
            >
              <Radio label="Low" value={TicketPriority.LOW} />
              <Radio label="Medium" value={TicketPriority.MEDIUM} />
              <Radio label="High" value={TicketPriority.HIGH} />
              <Radio label="Urgent" value={TicketPriority.URGENT} />
            </RadioGroup>
          </FormGroup>
        </div>

        <div className={Classes.DIALOG_FOOTER}>
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            <Button onClick={() => tickets.setCreateDialogOpen(false)}>Cancel</Button>
            <Button 
              intent={Intent.PRIMARY} 
              onClick={handleCreateTicket}
              loading={tickets.loading}
            >
              Submit Issue
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Ticket Details Dialog */}
      <Dialog
        isOpen={!!selectedTicketId && !!selectedTicket}
        onClose={() => setSelectedTicketId(null)}
        title={selectedTicket?.title}
        className="ticket-details-dialog"
        style={{ width: '600px', maxWidth: '90vw' }}
      >
        {selectedTicket && (
          <>
            <div className={Classes.DIALOG_BODY}>
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px' }}>
                  <Icon 
                    icon={getTypeIcon(selectedTicket.type)} 
                    size={16}
                    color={getStatusColor(selectedTicket.status)}
                  />
                  <Tag 
                    minimal 
                    intent={getPriorityColor(selectedTicket.priority)}
                  >
                    {selectedTicket.priority}
                  </Tag>
                  {tickets.isAdmin && (
                    <Popover
                      content={
                        <Menu>
                          <MenuItem
                            text="Backlog"
                            icon={getStatusIcon(TicketStatus.BACKLOG)}
                            onClick={() => {
                              handleStatusChange(selectedTicket.id, TicketStatus.BACKLOG);
                            }}
                            active={selectedTicket.status === TicketStatus.BACKLOG}
                          />
                          <MenuItem
                            text="In Progress"
                            icon={getStatusIcon(TicketStatus.IN_PROGRESS)}
                            onClick={() => {
                              handleStatusChange(selectedTicket.id, TicketStatus.IN_PROGRESS);
                            }}
                            active={selectedTicket.status === TicketStatus.IN_PROGRESS}
                          />
                          <MenuItem
                            text="Resolved"
                            icon={getStatusIcon(TicketStatus.RESOLVED)}
                            onClick={() => {
                              handleStatusChange(selectedTicket.id, TicketStatus.RESOLVED);
                            }}
                            active={selectedTicket.status === TicketStatus.RESOLVED}
                          />
                        </Menu>
                      }
                      position={Position.BOTTOM}
                    >
                      <Button 
                        text={selectedTicket.status.replace('_', ' ')}
                        rightIcon={IconNames.CARET_DOWN}
                        small
                        minimal
                      />
                    </Popover>
                  )}
                  {!tickets.isAdmin && (
                    <Tag>
                      {selectedTicket.status.replace('_', ' ')}
                    </Tag>
                  )}
                </div>

                <p style={{ marginBottom: '16px', lineHeight: '1.5' }}>
                  {selectedTicket.description}
                </p>

                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '4px', 
                  fontSize: '12px', 
                  color: 'var(--text-muted)',
                  marginBottom: '16px',
                  paddingTop: '12px',
                  borderTop: '1px solid var(--border-color)'
                }}>
                  <span>Created by: {selectedTicket.creator?.name || selectedTicket.creator?.email || 'Unknown'}</span>
                  <span>Created: {new Date(selectedTicket.createdAt).toLocaleDateString()}</span>
                  {selectedTicket.assignee && (
                    <span>Assigned to: {selectedTicket.assignee.name || selectedTicket.assignee.email}</span>
                  )}
                </div>
              </div>

              {/* Comments section */}
              <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border-color)' }}>
                <h4 style={{ marginBottom: '12px', fontSize: '14px', fontWeight: 600 }}>Comments</h4>
                <Comments
                  type="ticket"
                  entityId={selectedTicket.id}
                />
              </div>
            </div>

            <div className={Classes.DIALOG_FOOTER}>
              <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                {tickets.isAdmin && (
                  <Button 
                    icon={IconNames.TRASH}
                    intent={Intent.DANGER}
                    onClick={() => handleDeleteTicket(selectedTicket.id)}
                  >
                    Delete Ticket
                  </Button>
                )}
                <div style={{ flex: 1 }} />
                <Button onClick={() => setSelectedTicketId(null)}>Close</Button>
              </div>
            </div>
          </>
        )}
      </Dialog>
    </div>
  );
});

export default IssueTracking;
